'use client'

import { useState } from 'react'
import { ProductionTimeline } from '../components/ProductionTimeline'
import { ProductionUsage } from '../components/ProductionUsage'
import { Frame } from '../components/ProductionShared'
import { LineageTimeline } from './LineageTimeline'

// What Martini handles for a studio's tools: three things that are hard to build yourself. One tab each,
// its claim inside the tab, and a live visual to play with. The visuals are the homepage's own, so the
// product reads the same everywhere.
const REASONS = [
  {
    id: 'multiplayer',
    name: 'One canvas and timeline',
    claim: 'Your team and your agents work on the same shots, live.',
    visual: <ProductionTimeline />,
  },
  {
    id: 'provenance',
    name: 'How every shot was made',
    claim: 'Every asset records which model or tool made it, and every change since.',
    visual: <LineageTimeline />,
    wide: true,
  },
  {
    id: 'billing',
    name: 'What every shot cost',
    claim: 'Spend by person, agent, project and model.',
    visual: <ProductionUsage />,
  },
]

// The provenance graph's story, for screens too narrow to draw it.
const PROVENANCE_CHAIN = [
  { title: 'References', line: 'The cast reference and the voice-over take.' },
  { title: 'Takes', line: 'Two generations; take 2 is picked.' },
  { title: 'Edit', line: 'Lip sync on the picked take.' },
  { title: 'In the cut', line: 'Shot 4, with every step above it on record.' },
]

export function WhyConnect() {
  const [active, setActive] = useState(REASONS[0].id)
  const reason = REASONS.find(r => r.id === active) ?? REASONS[0]

  return (
    <div>
      {/* Each tab says what it shows, so the row reads as three choices before anyone clicks. */}
      <div role="tablist" aria-label="What Martini handles" className="grid gap-2 md:grid-cols-3">
        {REASONS.map(r => {
          const on = r.id === active
          return (
            <button
              key={r.id}
              type="button"
              role="tab"
              aria-selected={on}
              onClick={() => setActive(r.id)}
              className={`group cursor-pointer rounded-2xl border p-5 text-start transition-colors ${
                on
                  ? 'border-white/35 bg-white/[0.07]'
                  : 'border-white/[0.09] bg-white/[0.02] hover:border-white/25 hover:bg-white/[0.05]'
              }`}
            >
              <span
                className={`block text-[17px] font-medium tracking-[-0.01em] ${on ? 'text-white' : 'text-white/70 group-hover:text-white'}`}
              >
                {r.name}
              </span>
              <span className={`mt-1.5 block text-[14px] leading-snug ${on ? 'text-white/60' : 'text-white/40'}`}>
                {r.claim}
              </span>
            </button>
          )
        })}
      </div>
      {/* Keyed so each tab's visual mounts fresh. The provenance graph needs ~800px. */}
      <Frame key={reason.id} className={`mt-6 ${'wide' in reason && reason.wide ? 'hidden md:block' : ''}`}>
        {reason.visual}
      </Frame>
      {/* Phones get the provenance chain as a list. */}
      {'wide' in reason && reason.wide ? (
        <ol className="mt-6 space-y-2 md:hidden">
          {PROVENANCE_CHAIN.map((step, i) => (
            <li
              key={step.title}
              className="flex gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-[15px]"
            >
              <span className="text-white/50">{i + 1}</span>
              <span>
                <span className="font-medium text-white">{step.title}</span>
                <span className="block text-white/60">{step.line}</span>
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  )
}
